import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@atgis/ui";
import { Building2, Compass, HardHat, Users } from "lucide-react";
import { SectionHeading } from "../components/SectionHeading";

const ROLES = [
  {
    icon: Building2,
    badge: "Enterprise",
    title: "Altyapı kurumları",
    summary:
      "Su, doğalgaz, telekom, elektrik dağıtım ve belediyeler. Şebekeyi 'Mevcut Altyapı' olarak yönetir, 'Altyapı Projesi'lerle saha çalışmasını organize eder.",
    bullets: [
      "Kurumsal veri modeli, partner kataloğu ve ekip hiyerarşisi",
      "Saha yüklenicilerine Free tier hesapla yetki dağıtımı — yükleniciye ne plan, ne depolama maliyeti bindirmezsiniz",
      "Versiyonlu revizyon onay zinciriyle saha hatalarının ana şebekeye sızmasını önleme",
      "Mevcut Altyapı ve Altyapı Projesi'lere yüklenen tüm ekler enterprise kotasından (500 GB, artırılabilir) düşer",
    ],
  },
  {
    icon: Compass,
    badge: "Pro",
    title: "Harita ve CBS mühendislik ofisleri",
    summary:
      "Altyapı sahibi olmasanız da müşterileriniz için ölçüm, etüt ve haritalama yapıyorsanız RADGIS Workspace tam size göre.",
    bullets: [
      "Pro hesabınızla bir workspace açın, ekip üyelerini Free veya Basic hesaplarıyla davet edin",
      "Sahadaki ekiplerin ölçüm verisi ofisteki mühendislere eş zamanlı düşer",
      "Her proje için iş bölümü, paylaşım yetkileri ve depolama tek yerden yönetilir",
      "Workspace projelerine yüklenen tüm ekler workspace sahibinin kotasından (10 GB, artırılabilir) düşer",
    ],
  },
  {
    icon: HardHat,
    badge: "Free",
    title: "Saha yüklenicileri ve ölçüm ekipleri",
    summary:
      "Bir altyapı kurumunun veya mühendislik ofisinin yüklenicisi olarak çalışıyorsanız RADGIS'i ücretsiz kullanabilirsiniz. Plan satın almanıza gerek yok.",
    bullets: [
      "Free tier ile kayıt olun — kart bilgisi yok, süre kısıtı yok",
      "Müşteriniz sizi kendi enterprise'ına üye olarak ekler",
      "Yetki verilen bölgelerdeki Altyapı Projesi'lerde sahada ölçüm ve çizim yaparsınız",
    ],
  },
];

export function Audiences() {
  return (
    <section id="kimler-icin" className="border-b border-border/60">
      <div className="container py-20 md:py-28">
        <SectionHeading
          eyebrow="Kimler İçin?"
          title={
            <>
              Tek platform, <span className="gradient-text">üç farklı rol</span>.
            </>
          }
          description="RADGIS yalnızca altyapı sahibi şirketler için değil. Onların yüklenicileri ve bağımsız mühendislik ofisleri de aynı omurgada çalışır — her rol kendi planında, ek maliyet birikmeden."
        />

        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {ROLES.map(({ icon: Icon, badge, title, summary, bullets }) => (
            <Card key={title} className="flex h-full flex-col">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="grid h-10 w-10 place-items-center rounded-lg bg-muted">
                    <Icon className="h-5 w-5 text-foreground/80" />
                  </div>
                  <span className="rounded-full border border-border/60 bg-background px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                    {badge}
                  </span>
                </div>
                <CardTitle className="mt-4 text-xl">{title}</CardTitle>
                <CardDescription className="text-base">{summary}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {bullets.map((b) => (
                    <li key={b} className="flex gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <CrossTierCollaboration />

        <div className="mt-10 rounded-2xl border border-primary/40 bg-primary/5 p-6 md:p-8">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-6">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-primary/15">
              <Users className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="text-base font-semibold text-foreground md:text-lg">
                Maliyet baskısı yok. Plan dayatması yok.
              </div>
              <p className="mt-1 text-sm text-muted-foreground md:text-base">
                Sahadaki yüklenici <span className="font-medium text-foreground">Free</span>,
                ofisteki mühendis <span className="font-medium text-foreground">Pro</span>, projeyi
                yöneten kurum <span className="font-medium text-foreground">Enterprise</span> —
                hepsi aynı projede, aynı haritada, aynı veri üzerinde çalışır. Bir tarafın diğerine
                plan satın alma şartı koyduğu bir model değil bu.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const TIER_DOT: Record<string, string> = {
  Free: "bg-muted-foreground/60",
  Basic: "bg-[hsl(var(--project-plain))]",
  Pro: "bg-[hsl(var(--project-sub))]",
  Enterprise: "bg-[hsl(var(--project-master))]",
};

const COLLAB_ROWS = [
  {
    owner: "Enterprise",
    creates: "Mevcut Altyapı + Altyapı Projesi",
    summary:
      "Kurumsal şebekenin sahibi. Saha ekiplerini ve yüklenicileri enterprise üyesi olarak ekler.",
    members: ["Free", "Basic", "Pro", "Enterprise"],
    note: "Saha yüklenicisi Free hesapla bile Altyapı Projesi'lerde çalışabilir.",
  },
  {
    owner: "Pro",
    creates: "Workspace + serbest projeler",
    summary:
      "Mühendislik ofisi. Workspace'ine ekip üyesi davet eder, sahadan ofise akan ölçümü tek yerden yönetir.",
    members: ["Free", "Basic", "Pro", "Enterprise"],
    note: "Workspace üyesi başka bir kurumun enterprise üyesi de olabilir.",
  },
  {
    owner: "Basic",
    creates: "Serbest proje paylaşımı",
    summary:
      "Bireysel kullanıcı veya küçük ekip. Serbest projelerini düzenleme veya görüntüleme yetkisiyle paylaşır.",
    members: ["Free", "Basic", "Pro", "Enterprise"],
    note: "Karşı taraf hangi planda olursa olsun çalışmaya katılabilir.",
  },
];

function CrossTierCollaboration() {
  return (
    <div className="mt-10 overflow-hidden rounded-2xl border border-border/60 bg-background">
      <div className="border-b border-border/60 bg-muted/40 px-6 py-4">
        <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">
          Kim kiminle çalışır?
        </div>
        <div className="mt-1 text-base font-medium text-foreground md:text-lg">
          Plan, projeyi <em className="not-italic font-semibold">açan</em> kişiyi belirler —
          üyelerin planı değil.
        </div>
      </div>

      <div className="hidden grid-cols-[1.1fr_1.3fr_1fr] border-b border-border/60 bg-muted/20 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground md:grid">
        <div>Sahip & yarattığı</div>
        <div>Davet edebileceği üye planları</div>
        <div>Sonuç</div>
      </div>

      {COLLAB_ROWS.map((row, idx) => (
        <div
          key={row.owner}
          className={
            "grid gap-4 px-6 py-5 md:grid-cols-[1.1fr_1.3fr_1fr] md:items-center md:gap-6 " +
            (idx > 0 ? "border-t border-border/60" : "")
          }
        >
          <div>
            <div className="flex items-center gap-2">
              <span className={`h-2 w-2 rounded-full ${TIER_DOT[row.owner]}`} />
              <span className="text-sm font-semibold tracking-wide text-foreground">
                {row.owner}
              </span>
            </div>
            <div className="mt-1.5 text-sm text-foreground/80">{row.creates}</div>
            <p className="mt-1 text-xs text-muted-foreground">{row.summary}</p>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {row.members.map((m) => (
              <span
                key={m}
                className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-card px-2.5 py-1 text-[11px] font-medium text-foreground/80"
              >
                <span className={`h-1.5 w-1.5 rounded-full ${TIER_DOT[m]}`} />
                {m}
              </span>
            ))}
          </div>

          <div className="text-xs text-muted-foreground md:text-sm">{row.note}</div>
        </div>
      ))}
    </div>
  );
}
